/**
 * @file DeviceValueRow.tsx
 * @description Строка «на телефоне N px · Своё ›» под числом ПК (phase19.16,
 * партия SB120, этап 1; uiux-sb120-stage1 §2.2, §7 «DeviceValueRow»).
 * Владелец видит, сколько будет на телефоне, не открывая телефон; «Своё ›»
 * раскрывает поле телефона (`children`) со «↺ по доле». Своё значение уже
 * есть — поле раскрыто сразу.
 */

import { ChevronRight } from "lucide-react";
import { useId, useState, type ReactNode } from "react";

import { cn } from "@/lib/utils";

interface Props {
  /** «на телефоне 29 px (30 %)». */
  summary: string;
  /** У телефона своё значение. */
  own: boolean;
  /** Подпись поля для чтеца экрана: «Внутри сверху на телефоне». */
  label: string;
  children: ReactNode;
}

export function DeviceValueRow({ summary, own, label, children }: Props) {
  const [opened, setOpened] = useState(false);
  const open = own || opened;
  const panelId = useId();
  return (
    <div className="flex min-w-0 flex-col gap-1">
      <div className="flex min-w-0 items-center justify-between gap-2">
        <span className="type-caption min-w-0 text-on-surface-variant">{summary}</span>
        {own ? null : (
          <button
            type="button"
            aria-expanded={open}
            aria-controls={panelId}
            aria-label={`${label}: своё значение`}
            onClick={() => setOpened(!opened)}
            className="inline-flex h-12 shrink-0 items-center gap-1 rounded-lg px-2 text-sm font-medium text-primary outline-none hover:bg-surface-low focus-visible:ring-2 focus-visible:ring-ring can-hover:h-8"
          >
            Своё
            <ChevronRight aria-hidden className={cn("size-4 transition-transform", open && "rotate-90")} />
          </button>
        )}
      </div>
      {open ? (
        <div id={panelId} className="border-l-2 border-outline-variant pl-3">
          {children}
        </div>
      ) : null}
    </div>
  );
}
